import React, { useState, useEffect } from 'react';

interface Skill {
  id: number;
  name: string;
  job_listing_total_count: number;
}

interface SkillGroup {
  kind: string;
  kind_label: string;
  skills: Skill[];
}

interface SkillsFilterModalProps {
  show: boolean;
  onClose: () => void;
  onApply: (skillIds: number[]) => void;
  selectedSkillIds: number[];
}

const SkillsFilterModal: React.FC<SkillsFilterModalProps> = ({
  show,
  onClose,
  onApply,
  selectedSkillIds
}) => {
  const [skillGroups, setSkillGroups] = useState<SkillGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [tempSelected, setTempSelected] = useState<number[]>(selectedSkillIds);

  useEffect(() => {
    if (show) {
      fetchSkills();
      setTempSelected(selectedSkillIds);
    }
  }, [show, selectedSkillIds]);

  const fetchSkills = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/skills');
      const data = await response.json();
      setSkillGroups(data);
    } catch (error) {
      console.error('Failed to fetch skills:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (skillId: number) => {
    const newSelected = tempSelected.includes(skillId)
      ? tempSelected.filter(id => id !== skillId)
      : [...tempSelected, skillId];
    
    setTempSelected(newSelected);
  };

  const handleSelectAll = () => {
    const allIds = skillGroups.flatMap(group => group.skills.map(skill => skill.id));
    setTempSelected(allIds);
  };

  const handleClearAll = () => {
    setTempSelected([]);
  };

  const handleApply = () => {
    onApply(tempSelected);
    onClose();
  };

  if (!show) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="modal-backdrop fade show" 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 10060 
        }}
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className="modal fade show d-block" 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 10061,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        tabIndex={-1}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div className="modal-dialog modal-dialog-scrollable modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">スキルを選択</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onClose}
                aria-label="閉じる"
              />
            </div>
            
            <div className="modal-body">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">読み込み中...</span>
                  </div>
                </div>
              ) : (
                <>
                  {/* 選択/解除ボタン */}
                  <div className="d-flex gap-2 mb-3">
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      onClick={handleSelectAll}
                    >
                      一括選択
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-danger"
                      onClick={handleClearAll}
                    >
                      一括解除
                    </button>
                    <div className="ms-auto text-muted small">
                      {tempSelected.length}件選択中
                    </div>
                  </div>

                  {/* スキルグループごとのリスト */}
                  {skillGroups.map((group) => {
                    const groupSkillIds = group.skills.map(skill => skill.id);
                    const isGroupAllSelected = groupSkillIds.length > 0 && 
                      groupSkillIds.every(id => tempSelected.includes(id));
                    const isGroupPartiallySelected = groupSkillIds.some(id => tempSelected.includes(id));
                    
                    return (
                      <div key={group.kind} className="mb-4">
                        <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                          <h6 className="mb-0">{group.kind_label}</h6>
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => {
                                const newSelected = [...new Set([...tempSelected, ...groupSkillIds])];
                                setTempSelected(newSelected);
                              }}
                              disabled={isGroupAllSelected}
                            >
                              一括選択
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => {
                                const newSelected = tempSelected.filter(id => !groupSkillIds.includes(id));
                                setTempSelected(newSelected);
                              }}
                              disabled={!isGroupPartiallySelected}
                            >
                              一括解除
                            </button>
                          </div>
                        </div>
                        <div className="row g-2">
                          {group.skills.map(skill => (
                            <div key={skill.id} className="col-6 col-md-4 col-lg-3">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`skill-${skill.id}`}
                                  checked={tempSelected.includes(skill.id)}
                                  onChange={() => handleToggle(skill.id)}
                                />
                                <label className="form-check-label" htmlFor={`skill-${skill.id}`} style={{ cursor: 'pointer' }}>
                                  {skill.name} ({skill.job_listing_total_count})
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={onClose}
              >
                キャンセル
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={handleApply}
              >
                この条件で検索
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SkillsFilterModal;